/**
 * Generic sortable list (dnd-kit) with keyboard support and move up/down
 * buttons as an accessible fallback.
 */
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ChevronDown, ChevronUp, GripVertical } from "lucide-react";
import type { ReactElement, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/shared/ui/button";

/** Props for `SortableList`. */
export interface SortableListProps<T> {
  items: T[];
  getId: (item: T) => string;
  onChange: (items: T[]) => void;
  renderItem: (item: T, index: number) => ReactNode;
  className?: string;
}

function Row({
  id,
  index,
  total,
  children,
  onMove,
}: {
  id: string;
  index: number;
  total: number;
  children: ReactNode;
  onMove: (from: number, to: number) => void;
}): ReactElement {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });
  const style = { transform: CSS.Transform.toString(transform), transition };
  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`flex items-start gap-2 rounded-md border bg-card p-2 ${isDragging ? "opacity-60 shadow-lg" : ""}`}
    >
      <button
        type="button"
        className="mt-2 shrink-0 touch-none cursor-grab text-muted-foreground"
        aria-label={t("app.dragToReorder")}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <div className="min-w-0 flex-1">{children}</div>
      <div className="flex flex-col">
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="h-6 w-6"
          aria-label={t("app.moveUp")}
          disabled={index === 0}
          onClick={() => onMove(index, index - 1)}
        >
          <ChevronUp className="h-3 w-3" />
        </Button>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="h-6 w-6"
          aria-label={t("app.moveDown")}
          disabled={index === total - 1}
          onClick={() => onMove(index, index + 1)}
        >
          <ChevronDown className="h-3 w-3" />
        </Button>
      </div>
    </li>
  );
}

/** Sortable vertical list. */
export function SortableList<T>({
  items,
  getId,
  onChange,
  renderItem,
  className,
}: SortableListProps<T>): ReactElement {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const ids = items.map(getId);
  const move = (from: number, to: number): void => onChange(arrayMove(items, from, to));
  const onDragEnd = (e: DragEndEvent): void => {
    const { active, over } = e;
    if (over && active.id !== over.id) {
      move(ids.indexOf(String(active.id)), ids.indexOf(String(over.id)));
    }
  };
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <ul className={className ?? "space-y-2"}>
          {items.map((item, i) => {
            const id = getId(item);
            return (
              <Row key={id} id={id} index={i} total={items.length} onMove={move}>
                {renderItem(item, i)}
              </Row>
            );
          })}
        </ul>
      </SortableContext>
    </DndContext>
  );
}

/** Stable client-side id for new rows. */
export function localId(): string {
  return (
    globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`
  );
}
